import { Shortcuts, type AttachmentView, type AutocompleteMenuState, type CommandHint, type ComposerChipContext, type FormatKind, type PickedFile } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { Button, Card, Checkbox, EmojiPicker, FilePicker, Icon, IconButton, Menu, Popover, Sheet, Spacer, Stack, Text, TextField, type MenuEntry, type TextFieldKey } from "../primitives/index.ts";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";
import { AttachmentChips } from "./AttachmentChips.tsx";
import { AutocompleteMenu } from "./AutocompleteMenu.tsx";
import { ComposerField, type ComposerFormatter } from "./ComposerField.tsx";

/** Slack's formatting bar, in its order (groups split by a gap), with each button's shortcut. */
const FORMAT_GROUPS: readonly (readonly { kind: FormatKind; icon: IconName; keys: readonly string[] }[])[] = [
  [
    { kind: "bold", icon: "bold", keys: ["mod", "B"] },
    { kind: "italic", icon: "italic", keys: ["mod", "I"] },
    { kind: "strike", icon: "strike", keys: ["mod", "shift", "X"] },
  ],
  [{ kind: "link", icon: "link", keys: ["mod", "shift", "U"] }],
  [
    { kind: "orderedList", icon: "listNumbered", keys: ["mod", "shift", "7"] },
    { kind: "bulletList", icon: "list", keys: ["mod", "shift", "8"] },
  ],
  [{ kind: "quote", icon: "quote", keys: ["mod", "shift", "9"] }],
  [
    { kind: "code", icon: "code", keys: ["mod", "shift", "C"] },
    { kind: "codeBlock", icon: "fileCode", keys: ["mod", "alt", "shift", "C"] },
  ],
];

const APPLE = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/**
 * Show / hide formatting (the Aa button), remembered on this device and shared by every open composer (Slack keeps
 * it as a preference; shown by default).
 */
const FORMATTING_KEY = "bundle.composer.formatting";
const formattingListeners = new Set<() => void>();
function readFormattingShown(): boolean {
  try {
    return globalThis.localStorage?.getItem(FORMATTING_KEY) !== "hidden";
  } catch {
    return true;
  }
}
let formattingShown = readFormattingShown();
function setFormattingShown(shown: boolean): void {
  formattingShown = shown;
  try {
    globalThis.localStorage?.setItem(FORMATTING_KEY, shown ? "shown" : "hidden");
  } catch {
    // Storage blocked: the choice lasts this session.
  }
  for (const l of formattingListeners) l();
}
function useFormattingShown(): boolean {
  return useSyncExternalStore(
    (l) => {
      formattingListeners.add(l);
      return () => formattingListeners.delete(l);
    },
    () => formattingShown,
    () => true,
  );
}

/** Slack's formatting buttons (also the mail composer's Aa bar). */
export function FormattingBar({ onFormat, testID }: { onFormat: (kind: FormatKind) => void; testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1" wrap testID={testID}>
      {FORMAT_GROUPS.map((group, i) => (
        <Stack key={i} direction="horizontal" align="center" gap="0.5">
          {group.map((b) => (
            <IconButton
              key={b.kind}
              icon={b.icon}
              size="sm"
              accessibilityLabel={`${t(`composer.format.${b.kind}`)} ${Shortcuts.keyCaps(b.keys, APPLE).join(APPLE ? "" : "+")}`}
              onPress={() => onFormat(b.kind)}
              // Keep the field's focus and selection: the button formats what is selected.
              onMouseDown={(e: { preventDefault: () => void }) => e.preventDefault()}
              testID={`${testID}-${b.kind}`}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

export type ComposerProps = {
  value: string;
  onChange: (text: string) => void;
  onSend: () => void;
  /**
   * Shows the mentions in `value` (`@Fizz`, `<@id>`, `#design`, `@here`) as chips, resolved against this directory
   * (core logic/composerText); absent = plain text.
   */
  chips?: ComposerChipContext;
  placeholder: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Files attached to the draft (chips above the field). */
  attachments?: AttachmentView[];
  /** Picked (attach button), pasted or dropped files; absent = no attaching here. */
  onAddFiles?: (files: PickedFile[]) => void;
  onRemoveAttachment?: (id: string) => void;
  onRetryAttachment?: (id: string) => void;
  /** Send is enabled (text or files); defaults to "the text isn't empty". */
  canSend?: boolean;
  /** Suggestions for the token being typed (ChannelViewModel `autocomplete`), above the field; absent = closed. */
  autocomplete?: AutocompleteMenuState;
  /** Arguments of the slash command the draft starts with, shown above the text. */
  commandHint?: CommandHint;
  /** Caret placement requested by the view model (after a completion or the @ button). */
  selection?: { start: number; end: number; seq: number };
  onSelectionChange?: (start: number, end: number) => void;
  /** ↑ ↓ Enter Tab Esc while typing; return true when the menu took the key. */
  onAutocompleteKey?: (key: TextFieldKey) => boolean;
  onAutocompleteChoose?: (index: number) => void;
  onAutocompleteHover?: (index: number) => void;
  /** Click outside the menu (Escape arrives through onAutocompleteKey). */
  onAutocompleteDismiss?: () => void;
  /** The @ button; absent = it just types "@" at the end. */
  onMention?: () => void;
  /**
   * Replaces the emoji popover with a richer picker (Emoji · Stickers · GIFs); called with the composer's emoji
   * inserter. Absent = the plain emoji picker.
   */
  renderMediaPicker?: (insertEmoji: (emoji: string) => void) => ReactNode;
  mediaPickerOpen?: boolean;
  onMediaPickerOpenChange?: (open: boolean) => void;
  /** Phones: the picker is a bottom sheet (with this title) instead of a popover. */
  mediaPickerSheet?: boolean;
  mediaPickerTitle?: string;
  /** Thread replies: the "Also send to #design" checkbox between the field and the toolbar; absent = none. */
  alsoSendLabel?: string;
  alsoSend?: boolean;
  onAlsoSendChange?: (checked: boolean) => void;
  /**
   * Edit mode (editing a sent message in place): the toolbar shows the emoji picker, a hint and Cancel / Save instead
   * of attach, format and send; Escape (when the menu doesn't take it) and Cancel call this. `onSend` saves, and
   * `canSend` false (empty text) disables Save and says why.
   */
  onCancel?: () => void;
  /** Slack's send-button menu ("Schedule for later": Tomorrow at 9:00 AM, Monday…, Custom time); absent = none. */
  sendMenu?: { label: string; items: MenuEntry[] };
  testID: string;
};

/** Rounded bordered card: auto-growing field, toolbar (@, attach, emoji, Aa) and a round send button. Works offline (queues). */
export function Composer(p: ComposerProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const [picking, setPicking] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const formatter = useRef<ComposerFormatter | null>(null);
  const formattingOn = useFormattingShown();
  // Formatting works on the rich field (mention chips on); the plain field has no bar.
  const showFormatting = formattingOn && p.chips !== undefined;
  const empty = p.canSend === undefined ? p.value.trim() === "" : !p.canSend;
  const attachments = p.attachments ?? [];
  const editing = p.onCancel !== undefined;
  const onKey = (key: TextFieldKey): boolean => {
    if (p.onAutocompleteKey?.(key) === true) return true;
    if (key !== "escape" || p.onCancel === undefined) return false;
    p.onCancel();
    return true;
  };
  const fieldKey = editing ? onKey : p.onAutocompleteKey;
  const insert = (s: string) => p.onChange(p.value === "" || p.value.endsWith(" ") || p.value.endsWith("\n") ? `${p.value}${s}` : `${p.value} ${s}`);
  return (
    <Stack radius="lg" border="border.strong" background="bg.canvas" testID={p.testID}>
      {attachments.length === 0 ? null : (
        <Stack paddingX="2.5" paddingY="2.5">
          <AttachmentChips
            attachments={attachments}
            onRemove={(id) => p.onRemoveAttachment?.(id)}
            onRetry={(id) => p.onRetryAttachment?.(id)}
            testID={`${p.testID}-attachments`}
          />
        </Stack>
      )}
      {showFormatting ? <FormattingBar onFormat={(kind) => formatter.current?.(kind)} testID={`${p.testID}-formatting`} /> : null}
      <Popover
        open={p.autocomplete !== undefined}
        onClose={() => p.onAutocompleteDismiss?.()}
        side="top"
        align="start"
        fill
        width="anchor"
        autoFocus={false}
        anchor={
          <Stack gap="1" paddingX="3.5" paddingY="2.5">
            {p.commandHint === undefined ? null : (
              <Stack direction="horizontal" align="center" gap="1.5" testID={`${p.testID}-command-hint`}>
                <Text variant="caption" weight="semibold" color="text.secondary">
                  {p.commandHint.label}
                </Text>
                <Text variant="caption" color="text.tertiary" lines={1}>
                  {tr(p.commandHint.usage)}
                </Text>
              </Stack>
            )}
            {p.chips === undefined ? (
              <TextField
                variant="plain"
                multiline
                value={p.value}
                onChange={p.onChange}
                onSubmit={p.onSend}
                placeholder={p.placeholder}
                autoFocus={p.autoFocus}
                onPasteFiles={p.onAddFiles}
                onSelectionChange={p.onSelectionChange}
                onKey={fieldKey}
                selection={p.selection}
                testID={`${p.testID}-input`}
              />
            ) : (
              <ComposerField
                value={p.value}
                onChange={p.onChange}
                onSubmit={p.onSend}
                chips={p.chips}
                placeholder={p.placeholder}
                autoFocus={p.autoFocus}
                onPasteFiles={p.onAddFiles}
                onSelectionChange={p.onSelectionChange}
                onKey={fieldKey}
                selection={p.selection}
                formatRef={formatter}
                testID={`${p.testID}-input`}
              />
            )}
          </Stack>
        }
      >
        {p.autocomplete === undefined ? null : (
          <AutocompleteMenu
            menu={p.autocomplete}
            onChoose={(i) => p.onAutocompleteChoose?.(i)}
            onHover={(i) => p.onAutocompleteHover?.(i)}
            testID={`${p.testID}-autocomplete`}
          />
        )}
      </Popover>
      {p.alsoSendLabel === undefined || p.onAlsoSendChange === undefined ? null : (
        <Stack paddingX="3.5">
          <Checkbox value={p.alsoSend === true} onChange={p.onAlsoSendChange} label={p.alsoSendLabel} testID={`${p.testID}-also-send`} />
        </Stack>
      )}
      {editing ? (
        <Stack direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5" wrap>
          <Popover
            open={picking}
            onClose={() => setPicking(false)}
            side="top"
            align="start"
            anchor={<IconButton icon="emoji" size="sm" accessibilityLabel={t("composer.emoji")} onPress={() => setPicking(true)} testID={`${p.testID}-emoji`} />}
          >
            <EmojiPicker
              locale={locale}
              searchPlaceholder={t("picker.emoji.search")}
              onSelect={(e) => {
                setPicking(false);
                insert(e);
              }}
            />
          </Popover>
          <Text variant="caption" color={empty ? "status.danger" : "text.tertiary"} lines={1} testID={`${p.testID}-hint`}>
            {empty ? t("message.editEmpty") : t("message.editHint")}
          </Text>
          <Spacer />
          <Button label={t("message.editCancel")} variant="secondary" size="sm" onPress={() => p.onCancel?.()} testID={`${p.testID}-cancel`} />
          <Button label={t("message.editSave")} variant="primary" size="sm" onPress={p.onSend} disabled={empty || p.disabled === true} testID={`${p.testID}-save`} />
        </Stack>
      ) : (
      <Stack direction="horizontal" align="center" gap="0.5" paddingX="2" paddingY="1.5">
        <IconButton icon="at" size="sm" accessibilityLabel={t("composer.mention")} onPress={() => (p.onMention === undefined ? insert("@") : p.onMention())} testID={`${p.testID}-mention`} />
        <IconButton
          icon="attach"
          size="sm"
          accessibilityLabel={t("composer.attach")}
          onPress={() => setChoosing(true)}
          disabled={p.onAddFiles === undefined || p.disabled === true}
          testID={`${p.testID}-attach`}
        />
        <FilePicker
          open={choosing}
          multiple
          onClose={() => setChoosing(false)}
          onPick={(files) => p.onAddFiles?.(files)}
          testID={`${p.testID}-file-input`}
        />
        {p.renderMediaPicker === undefined ? (
          <Popover
            open={picking}
            onClose={() => setPicking(false)}
            side="top"
            align="start"
            anchor={<IconButton icon="emoji" size="sm" accessibilityLabel={t("composer.emoji")} onPress={() => setPicking(true)} testID={`${p.testID}-emoji`} />}
          >
            <EmojiPicker
              locale={locale}
              searchPlaceholder={t("picker.emoji.search")}
              onSelect={(e) => {
                setPicking(false);
                insert(e);
              }}
            />
          </Popover>
        ) : p.mediaPickerSheet === true ? (
          <>
            <IconButton icon="emoji" size="sm" accessibilityLabel={t("composer.emoji")} onPress={() => p.onMediaPickerOpenChange?.(true)} testID={`${p.testID}-emoji`} />
            <Sheet
              open={p.mediaPickerOpen === true}
              onClose={() => p.onMediaPickerOpenChange?.(false)}
              title={p.mediaPickerTitle}
              placement="bottom"
              closeLabel={t("common.close")}
              testID={`${p.testID}-media-sheet`}
            >
              {p.renderMediaPicker(insert)}
            </Sheet>
          </>
        ) : (
          <Popover
            open={p.mediaPickerOpen === true}
            onClose={() => p.onMediaPickerOpenChange?.(false)}
            side="top"
            align="start"
            anchor={<IconButton icon="emoji" size="sm" accessibilityLabel={t("composer.emoji")} onPress={() => p.onMediaPickerOpenChange?.(true)} testID={`${p.testID}-emoji`} />}
          >
            {p.renderMediaPicker(insert)}
          </Popover>
        )}
        {p.chips === undefined ? null : (
          <IconButton
            icon="format"
            size="sm"
            accessibilityLabel={t(formattingOn ? "composer.format.hide" : "composer.format.show")}
            onPress={() => setFormattingShown(!formattingOn)}
            onMouseDown={(e: { preventDefault: () => void }) => e.preventDefault()}
            aria-pressed={formattingOn}
            testID={`${p.testID}-format`}
          />
        )}
        <Spacer />
        <IconButton icon="send" variant="filled" size="md" accessibilityLabel={t("composer.send")} onPress={p.onSend} disabled={empty || p.disabled === true} testID={`${p.testID}-send`} />
        {p.sendMenu === undefined ? null : (
          <Menu items={p.sendMenu.items} align="end" testID={`${p.testID}-send-menu`}>
            <IconButton icon="chevronDown" size="sm" accessibilityLabel={p.sendMenu.label} onPress={() => undefined} disabled={empty || p.disabled === true} testID={`${p.testID}-schedule`} />
          </Menu>
        )}
      </Stack>
      )}
    </Stack>
  );
}

export type ComposerNoticeProps = {
  /** "You can't send messages right now" (translated). */
  title: string;
  /** A second line ("Subscribe to switch sending back on"). */
  message?: string;
  /** Leading glyph (default lock). */
  icon?: IconName;
  /** At most one action ("Subscribe"); primary, since fixing it is the point. */
  action?: { label: string; onPress: () => void; testID: string };
  testID: string;
};

/**
 * The composer's read-only state: the composer is replaced (not disabled) by this notice when nobody can send here
 * (a read-only workspace after a trial or cancel). A card in the composer's slot, with an optional action.
 */
export function ComposerNotice({ title, message, icon = "lock", action, testID }: ComposerNoticeProps) {
  return (
    <Card padding="3" direction="horizontal" align="center" gap="3" testID={testID}>
      <Icon name={icon} size="icon.md" color="text.secondary" />
      <Stack grow gap="0.5">
        <Text variant="footnote" weight="medium">
          {title}
        </Text>
        {message === undefined ? null : (
          <Text variant="caption" color="text.tertiary">
            {message}
          </Text>
        )}
      </Stack>
      {action === undefined ? null : <Button label={action.label} onPress={action.onPress} variant="primary" size="sm" testID={action.testID} />}
    </Card>
  );
}
